/**
 * Runtime array, not a bare type union. A file arriving from outside has to be
 * *checked* against the allowed values, and a type union vanishes at runtime.
 * Every enumeration in this package is declared this way for that reason.
 */
export const INDICATOR_KINDS = [
    "sma",
    "ema",
    "rsi",
    "macd",
    "bbands",
    "stoch",
    "atr",
    "adx",
    // Moving averages
    "wma",
    "smma",
    "hma",
    "dema",
    "tema",
    "alma",
    "lsma",
    "mcginley",
    "kama",
    // Trend & channels
    "envelope",
    "donchian",
    "keltner",
    "psar",
    "supertrend",
    "ichimoku",
    // Oscillators
    "cci",
    "willr",
    "stochrsi",
    "roc",
    "mom",
    "trix",
    "ao",
    "ppo",
    "cmo",
    "tsi",
    "uo",
    "aroon",
    "vortex",
    "fisher",
    "coppock",
    "dpo",
    "crsi",
    // Volatility
    "stdev",
    "bbpct",
    "bbw",
    "chop",
    "hv",
    "massi",
    // Volume
    "volume",
    "vwap",
    "vwma",
    "obv",
    "ad",
    "adosc",
    "cmf",
    "mfi",
    "efi",
    "eom",
    "pvt",
    "vo",
    "bop",
    "rvi",
    // Arithmetic over the others — see formula.ts
    "formula",
];
export const PANE_KINDS = ["overlay", "subplot"];
export const LINE_STYLES = ["solid", "dashed", "dotted", "dashdot"];
/**
 * The series each kind produces. Single-output indicators name theirs `value`
 * rather than nothing, so an operand always addresses an output by name and
 * there is no special case for "the only one".
 */
export const INDICATOR_OUTPUTS = {
    sma: ["value"],
    ema: ["value"],
    rsi: ["value"],
    atr: ["value"],
    macd: ["macd", "signal", "histogram"],
    bbands: ["upper", "middle", "lower"],
    stoch: ["k", "d"],
    adx: ["adx", "pdi", "mdi"],
    wma: ["value"],
    smma: ["value"],
    hma: ["value"],
    dema: ["value"],
    tema: ["value"],
    alma: ["value"],
    lsma: ["value"],
    mcginley: ["value"],
    kama: ["value"],
    envelope: ["upper", "basis", "lower"],
    donchian: ["upper", "basis", "lower"],
    keltner: ["upper", "basis", "lower"],
    psar: ["value"],
    // `direction` is +1 in an uptrend and -1 in a downtrend (Pine's own sign is
    // the other way round). `long`/`short` are the line split by direction, so a
    // chart can colour it and a rule can say "while the long line exists".
    supertrend: ["value", "direction", "long", "short"],
    // What the cloud is *at this bar*: spans are the values computed
    // displacement-1 bars ago, exactly where a chart draws them. The lagging span
    // is left out on purpose — it is today's close drawn in the past, and reading
    // it at bar t would read the future.
    ichimoku: ["conversion", "base", "spanA", "spanB"],
    cci: ["value"],
    willr: ["value"],
    stochrsi: ["k", "d"],
    roc: ["value"],
    mom: ["value"],
    trix: ["value"],
    ao: ["value"],
    ppo: ["ppo", "signal", "histogram"],
    cmo: ["value"],
    tsi: ["tsi", "signal"],
    uo: ["value"],
    aroon: ["up", "down", "oscillator"],
    vortex: ["plus", "minus"],
    fisher: ["fisher", "trigger"],
    coppock: ["value"],
    dpo: ["value"],
    crsi: ["value"],
    stdev: ["value"],
    bbpct: ["value"],
    bbw: ["value"],
    chop: ["value"],
    hv: ["value"],
    massi: ["value"],
    volume: ["value", "ma"],
    vwap: ["value", "upper", "lower"],
    vwma: ["value"],
    obv: ["value", "ma"],
    ad: ["value", "ma"],
    adosc: ["value"],
    cmf: ["value"],
    mfi: ["value"],
    efi: ["value"],
    eom: ["value"],
    pvt: ["value", "ma"],
    vo: ["value"],
    bop: ["value"],
    rvi: ["rvi", "signal"],
    formula: ["value"],
};
/**
 * What a reference to a multi-output indicator means when it names no output.
 *
 * The two prototypes disagreed here, silently: one took the first key of the
 * outputs object, making an unnamed `bbands` reference mean the **upper** band,
 * while the other used this map, making it mean the **middle**. Same file,
 * different trades, no error anywhere.
 *
 * This map wins. `middle` is the band a moving-average strategy means when it
 * says "the Bollinger line", and `macd` is the MACD line rather than its
 * signal. An unnamed reference is still reported as *lossy* by the profile
 * check — it is a question the strategy did not answer — but at least both
 * engines now answer it the same way.
 */
export const DEFAULT_OUTPUT = {
    sma: "value",
    ema: "value",
    rsi: "value",
    atr: "value",
    macd: "macd",
    bbands: "middle",
    stoch: "k",
    adx: "adx",
    wma: "value",
    smma: "value",
    hma: "value",
    dema: "value",
    tema: "value",
    alma: "value",
    lsma: "value",
    mcginley: "value",
    kama: "value",
    envelope: "basis",
    donchian: "basis",
    keltner: "basis",
    psar: "value",
    supertrend: "value",
    ichimoku: "base",
    cci: "value",
    willr: "value",
    stochrsi: "k",
    roc: "value",
    mom: "value",
    trix: "value",
    ao: "value",
    ppo: "ppo",
    cmo: "value",
    tsi: "tsi",
    uo: "value",
    aroon: "oscillator",
    vortex: "plus",
    fisher: "fisher",
    coppock: "value",
    dpo: "value",
    crsi: "value",
    stdev: "value",
    bbpct: "value",
    bbw: "value",
    chop: "value",
    hv: "value",
    massi: "value",
    volume: "value",
    vwap: "value",
    vwma: "value",
    obv: "value",
    ad: "value",
    adosc: "value",
    cmf: "value",
    mfi: "value",
    efi: "value",
    eom: "value",
    pvt: "value",
    vo: "value",
    bop: "value",
    rvi: "rvi",
    formula: "value",
};
/** Where an indicator sits in a builder's menu, TradingView-style. */
export const INDICATOR_GROUPS = [
    "Moving averages",
    "Trend & channels",
    "Oscillators",
    "Volatility",
    "Volume",
    "Custom",
];
/** The output an operand means when it names none. */
export function defaultOutputFor(kind) {
    return DEFAULT_OUTPUT[kind];
}
/** Whether a kind produces more than one series, i.e. whether naming one matters. */
export function isMultiOutput(kind) {
    return INDICATOR_OUTPUTS[kind].length > 1;
}
/**
 * The slower charts an indicator may be computed on, for `IndicatorDef.timeframe`.
 *
 * Every one of them starts its bars on a whole multiple of its own length since
 * the Unix epoch, which is where exchanges start theirs, so a bar built from
 * the run's candles covers exactly the exchange's bar. That is not true of a
 * 3-day or weekly bar (Binance starts weeks on a Monday, the epoch was a
 * Thursday), so those are left out rather than built on the wrong days.
 */
export const INDICATOR_TIMEFRAMES = [
    "5m",
    "15m",
    "30m",
    "1h",
    "2h",
    "4h",
    "6h",
    "8h",
    "12h",
    "1d",
];
//# sourceMappingURL=indicator.js.map